/**
 * icons.ts — the curated Phosphor icon set shown in the picker modal.
 *
 * We don't expose all ~1500 Phosphor icons — that's noise. This is a hand-
 * picked set of model/supply-chain/finance/ops icons a modeller actually
 * reaches for (factory, truck, bank, storefront, users, package, brain...),
 * each with lowercase `tags` so the picker's search can match synonyms
 * ("truck", "delivery", "logistics" → Truck).
 *
 * The node stores the icon *name* (e.g. `"Factory"`); `renderNodeIcon` looks
 * it up here. An icon string that isn't in this table is rendered verbatim —
 * that's the backward-compat path for the emoji icons the existing
 * ghost_kitchen scenarios ship with.
 *
 * Imports are named so Vite tree-shakes the rest of the package out.
 */
import type { Icon as PhosphorIcon } from '@phosphor-icons/react';
import {
  Factory,
  Buildings,
  Bank,
  Storefront,
  Warehouse,
  Truck,
  Airplane,
  AirplaneTilt,
  Boat,
  Train,
  Package,
  Archive,
  Tray,
  ShoppingCart,
  ShoppingCartSimple,
  Handbag,
  CreditCard,
  CurrencyCircleDollar,
  CurrencyDollar,
  CurrencyEur,
  CurrencyGbp,
  CurrencyBtc,
  PiggyBank,
  Coins,
  Wallet,
  Receipt,
  ReceiptX,
  FileText,
  ClipboardText,
  Notebook,
  Users,
  User,
  UserCircle,
  UsersFour,
  Person,
  PersonArmsSpread,
  AddressBook,
  Brain,
  Cpu,
  Robot,
  Gear,
  GearSix,
  GearFine,
  Wrench,
  Hammer,
  Toolbox,
  Lightning,
  Plug,
  BatteryCharging,
  Power,
  Fire,
  Flame,
  Drop,
  GasPump,
  Leaf,
  Tree,
  Recycle,
  Sun,
  Moon,
  Cloud,
  Globe,
  MapPin,
  MapTrifold,
  NavigationArrow,
  Path,
  Graph,
  ChartLine,
  ChartBar,
  ChartPie,
  ChartDonut,
  PresentationChart,
  Target,
  Trophy,
  Medal,
  Crown,
  Star,
  Warning,
  WarningCircle,
  CheckCircle,
  Prohibit,
  Stop,
  Play,
  Pause,
  ArrowRight,
  ArrowsLeftRight,
  MagnifyingGlass,
  Bell,
  BellRinging,
  Envelope,
  Phone,
  Chat,
  ChatCircle,
  VideoCamera,
  Camera,
  Microphone,
  Lock,
  Key,
  Shield,
  ShieldCheck,
  Fingerprint,
  Eye,
  EyeSlash,
  Code,
  Terminal,
  Database,
  HardDrives,
  CloudArrowUp,
  WifiHigh,
  Broadcast,
  Pulse,
  Heartbeat,
  Stethoscope,
  Crosshair,
  FirstAidKit,
  Pill,
  Prescription,
  Syringe,
  Dna,
  Atom,
  Flask,
  TestTube,
  Microscope,
  RocketLaunch,
  Car,
  CarProfile,
  Bicycle,
  Motorcycle,
  Drone,
  Crane,
  HardHat,
  Screwdriver,
  Ruler,
  Scales,
  Gavel,
  BuildingOffice,
  House,
  HouseLine,
  Building,
  Bed,
  Couch,
  Armchair,
  ForkKnife,
  Knife,
  CookingPot,
  Broom,
  Barbell,
  Football,
  SoccerBall,
  TennisBall,
  Basketball,
  Volleyball,
  MusicNote,
  Guitar,
  PaintBrush,
  Palette,
  PencilSimple,
  Pen,
  Highlighter,
  Stamp,
  PaperPlaneTilt,
} from '@phosphor-icons/react';

export interface IconEntry {
  /** The Phosphor export name — what gets stored on `node.icon`. */
  name: string;
  /** Lowercase search aliases. */
  tags: string[];
  Component: PhosphorIcon;
}

// Tag shortcuts: keys folded into the icon's tags at build time.
const T = (...tags: string[]) => tags;

export const ICONS: IconEntry[] = [
  { name: 'Factory', tags: T('factory', 'plant', 'production', 'manufacturing', 'industry'), Component: Factory },
  { name: 'Buildings', tags: T('buildings', 'offices', 'complex'), Component: Buildings },
  { name: 'BuildingOffice', tags: T('office', 'building', 'corporate'), Component: BuildingOffice },
  { name: 'Building', tags: T('building'), Component: Building },
  { name: 'Bank', tags: T('bank', 'finance', 'institution'), Component: Bank },
  { name: 'Storefront', tags: T('store', 'shop', 'retail', ' storefront'), Component: Storefront },
  { name: 'Warehouse', tags: T('warehouse', 'storage', 'depot'), Component: Warehouse },
  { name: 'House', tags: T('house', 'home', 'residential'), Component: House },
  { name: 'HouseLine', tags: T('house', 'home'), Component: HouseLine },
  { name: 'Truck', tags: T('truck', 'delivery', 'logistics', 'shipping'), Component: Truck },
  { name: 'PackageTransport', tags: T('package', 'transport', 'shipping'), Component: Package },
  { name: 'Airplane', tags: T('plane', 'air', 'freight'), Component: Airplane },
  { name: 'AirplaneTilt', tags: T('plane', 'air'), Component: AirplaneTilt },
  { name: 'Boat', tags: T('boat', 'ship', 'sea', 'port'), Component: Boat },
  { name: 'Train', tags: T('train', 'rail'), Component: Train },
  { name: 'Car', tags: T('car', 'auto'), Component: Car },
  { name: 'CarProfile', tags: T('car', 'vehicle'), Component: CarProfile },
  { name: 'Bicycle', tags: T('bike', 'bicycle'), Component: Bicycle },
  { name: 'Motorcycle', tags: T('motorcycle', 'moto'), Component: Motorcycle },
  { name: 'Forklift', tags: T('forklift', 'warehouse'), Component: Warehouse },
  { name: 'Drone', tags: T('drone', 'uav', 'aerial'), Component: Drone },
  { name: 'Package', tags: T('package', 'box', 'parcel'), Component: Package },
  { name: 'Boxes', tags: T('boxes', 'inventory', 'bulk'), Component: Package },
  { name: 'Archive', tags: T('archive', 'box', 'storage'), Component: Archive },
  { name: 'Tray', tags: T('tray', 'inbox'), Component: Tray },
  { name: 'ShoppingCart', tags: T('cart', 'shop', 'buy'), Component: ShoppingCart },
  { name: 'ShoppingCartSimple', tags: T('cart', 'shop'), Component: ShoppingCartSimple },
  { name: 'Handbag', tags: T('bag', 'shopping'), Component: Handbag },
  { name: 'CreditCard', tags: T('card', 'payment', 'credit'), Component: CreditCard },
  { name: 'CurrencyCircleDollar', tags: T('money', 'dollar', 'usd', 'currency'), Component: CurrencyCircleDollar },
  { name: 'CurrencyDollar', tags: T('money', 'dollar', 'currency'), Component: CurrencyDollar },
  { name: 'CurrencyEuro', tags: T('euro', 'eur', 'currency'), Component: CurrencyEur },
  { name: 'CurrencyGbp', tags: T('pound', 'gbp', 'currency'), Component: CurrencyGbp },
  { name: 'CurrencyBtc', tags: T('bitcoin', 'btc', 'crypto'), Component: CurrencyBtc },
  { name: 'PiggyBank', tags: T('savings', 'piggy', 'bank'), Component: PiggyBank },
  { name: 'Coins', tags: T('coins', 'money', 'cash'), Component: Coins },
  { name: 'Wallet', tags: T('wallet', 'money'), Component: Wallet },
  { name: 'Receipt', tags: T('receipt', 'invoice'), Component: Receipt },
  { name: 'ReceiptX', tags: T('receipt', 'void', 'cancel'), Component: ReceiptX },
  { name: 'FileText', tags: T('document', 'file', 'text'), Component: FileText },
  { name: 'ClipboardText', tags: T('clipboard', 'notes', 'order'), Component: ClipboardText },
  { name: 'Notebook', tags: T('notebook', 'notes'), Component: Notebook },
  { name: 'Users', tags: T('users', 'people', 'team', 'crm'), Component: Users },
  { name: 'User', tags: T('user', 'person', 'account'), Component: User },
  { name: 'UserCircle', tags: T('user', 'profile', 'avatar'), Component: UserCircle },
  { name: 'UserGroup', tags: T('users', 'group', 'team'), Component: UsersFour },
  { name: 'Person', tags: T('person', 'individual'), Component: Person },
  { name: 'PersonArmsSpread', tags: T('person', 'agent', 'worker'), Component: PersonArmsSpread },
  { name: 'Addresses', tags: T('contacts', 'address', 'crm'), Component: AddressBook },
  { name: 'Brain', tags: T('brain', 'ai', 'ml', 'model', 'intelligence'), Component: Brain },
  { name: 'Cpu', tags: T('cpu', 'compute', 'chip'), Component: Cpu },
  { name: 'Robot', tags: T('robot', 'agent', 'automation'), Component: Robot },
  { name: 'Gear', tags: T('gear', 'settings', 'config'), Component: Gear },
  { name: 'GearSix', tags: T('gear', 'settings'), Component: GearSix },
  { name: 'GearFine', tags: T('gear', 'mechanism'), Component: GearFine },
  { name: 'Wrench', tags: T('wrench', 'tool', 'fix'), Component: Wrench },
  { name: 'Hammer', tags: T('hammer', 'build'), Component: Hammer },
  { name: 'Tools', tags: T('tools', 'workshop'), Component: Toolbox },
  { name: 'Bolt', tags: T('bolt', 'energy', 'electric', 'fast'), Component: Lightning },
  { name: 'Lightning', tags: T('lightning', 'energy', 'fast'), Component: Lightning },
  { name: 'Plug', tags: T('plug', 'power', 'connect'), Component: Plug },
  { name: 'BatteryCharging', tags: T('battery', 'charge', 'energy'), Component: BatteryCharging },
  { name: 'Power', tags: T('power', 'on', 'energy'), Component: Power },
  { name: 'Fire', tags: T('fire', 'hot', 'burn'), Component: Fire },
  { name: 'Flame', tags: T('flame', 'fire'), Component: Flame },
  { name: 'Drop', tags: T('drop', 'liquid', 'water'), Component: Drop },
  { name: 'GasPump', tags: T('gas', 'fuel', 'pump'), Component: GasPump },
  { name: 'Leaf', tags: T('leaf', 'eco', 'green'), Component: Leaf },
  { name: 'Tree', tags: T('tree', 'nature'), Component: Tree },
  { name: 'Recycle', tags: T('recycle', 'eco', 'reuse'), Component: Recycle },
  { name: 'Sun', tags: T('sun', 'day', 'light'), Component: Sun },
  { name: 'Moon', tags: T('moon', 'night'), Component: Moon },
  { name: 'Cloud', tags: T('cloud', 'sky', 'saas'), Component: Cloud },
  { name: 'Globe', tags: T('globe', 'world', 'web'), Component: Globe },
  { name: 'MapPin', tags: T('location', 'pin', 'place'), Component: MapPin },
  { name: 'MapTrifold', tags: T('map', 'geo', 'route'), Component: MapTrifold },
  { name: 'NavigationArrow', tags: T('navigation', 'arrow', 'direction'), Component: NavigationArrow },
  { name: 'Path', tags: T('path', 'route', 'flow'), Component: Path },
  { name: 'Graph', tags: T('graph', 'network', 'nodes'), Component: Graph },
  { name: 'ChartLine', tags: T('chart', 'line', 'trend', 'metric'), Component: ChartLine },
  { name: 'ChartBar', tags: T('chart', 'bar', 'metric'), Component: ChartBar },
  { name: 'ChartPie', tags: T('chart', 'pie'), Component: ChartPie },
  { name: 'ChartDonut', tags: T('chart', 'donut'), Component: ChartDonut },
  { name: 'PresentationChart', tags: T('presentation', 'chart', 'report'), Component: PresentationChart },
  { name: 'Target', tags: T('target', 'goal', 'kpi'), Component: Target },
  { name: 'Trophy', tags: T('trophy', 'win', 'award'), Component: Trophy },
  { name: 'Medal', tags: T('medal', 'award'), Component: Medal },
  { name: 'Crown', tags: T('crown', 'king', 'premium'), Component: Crown },
  { name: 'Star', tags: T('star', 'favorite', 'rating'), Component: Star },
  { name: 'Warning', tags: T('warning', 'alert', 'caution'), Component: Warning },
  { name: 'WarningCircle', tags: T('warning', 'alert', 'error'), Component: WarningCircle },
  { name: 'CheckCircle', tags: T('check', 'ok', 'success'), Component: CheckCircle },
  { name: 'Prohibit', tags: T('prohibit', 'block', 'stop'), Component: Prohibit },
  { name: 'Stop', tags: T('stop', 'halt'), Component: Stop },
  { name: 'Play', tags: T('play', 'start'), Component: Play },
  { name: 'Pause', tags: T('pause', 'hold'), Component: Pause },
  { name: 'ArrowRight', tags: T('arrow', 'right', 'forward'), Component: ArrowRight },
  { name: 'ArrowsLeftRight', tags: T('arrows', 'bidirectional', 'exchange'), Component: ArrowsLeftRight },
  { name: 'MagnifyingGlass', tags: T('search', 'find', 'inspect'), Component: MagnifyingGlass },
  { name: 'Bell', tags: T('bell', 'notification', 'alert'), Component: Bell },
  { name: 'BellRinging', tags: T('bell', 'alarm', 'notification'), Component: BellRinging },
  { name: 'Envelope', tags: T('email', 'mail', 'envelope'), Component: Envelope },
  { name: 'Phone', tags: T('phone', 'call'), Component: Phone },
  { name: 'Chat', tags: T('chat', 'message', 'talk'), Component: Chat },
  { name: 'ChatCircle', tags: T('chat', 'message'), Component: ChatCircle },
  { name: 'VideoCamera', tags: T('video', 'camera', 'call'), Component: VideoCamera },
  { name: 'Camera', tags: T('camera', 'photo'), Component: Camera },
  { name: 'Microphone', tags: T('mic', 'audio', 'voice'), Component: Microphone },
  { name: 'Lock', tags: T('lock', 'secure', 'closed'), Component: Lock },
  { name: 'Key', tags: T('key', 'auth'), Component: Key },
  { name: 'Shield', tags: T('shield', 'security', 'guard'), Component: Shield },
  { name: 'ShieldCheck', tags: T('shield', 'security', 'verified'), Component: ShieldCheck },
  { name: 'Fingerprint', tags: T('fingerprint', 'auth', 'identity'), Component: Fingerprint },
  { name: 'Eye', tags: T('eye', 'view', 'watch'), Component: Eye },
  { name: 'EyeSlash', tags: T('eye', 'hide', 'invisible'), Component: EyeSlash },
  { name: 'Code', tags: T('code', 'dev', 'script'), Component: Code },
  { name: 'Terminal', tags: T('terminal', 'cli', 'shell'), Component: Terminal },
  { name: 'Database', tags: T('database', 'db', 'store'), Component: Database },
  { name: 'HardDrives', tags: T('storage', 'disk', 'data'), Component: HardDrives },
  { name: 'CloudArrowUp', tags: T('upload', 'cloud', 'sync'), Component: CloudArrowUp },
  { name: 'Wifi', tags: T('wifi', 'network', 'wireless'), Component: WifiHigh },
  { name: 'SignalHigh', tags: T('signal', 'network', 'strong'), Component: Broadcast },
  { name: 'Broadcast', tags: T('broadcast', 'radio', 'signal'), Component: Broadcast },
  { name: 'Pulse', tags: T('pulse', 'heartbeat', 'live'), Component: Pulse },
  { name: 'Heartbeat', tags: T('heartbeat', 'health', 'pulse'), Component: Heartbeat },
  { name: 'Stethoscope', tags: T('health', 'medical', 'doctor'), Component: Stethoscope },
  { name: 'Crosshair', tags: T('crosshair', 'target', 'aim'), Component: Crosshair },
  { name: 'FirstAid', tags: T('firstaid', 'medical', 'kit'), Component: FirstAidKit },
  { name: 'Pill', tags: T('pill', 'medicine', 'drug'), Component: Pill },
  { name: 'DrugPrescription', tags: T('drug', 'prescription', 'pharma'), Component: Prescription },
  { name: 'Syringe', tags: T('syringe', 'injection', 'vaccine'), Component: Syringe },
  { name: 'Dna', tags: T('dna', 'biology', 'genetics'), Component: Dna },
  { name: 'Atom', tags: T('atom', 'science', 'physics'), Component: Atom },
  { name: 'Flask', tags: T('flask', 'lab', 'chemistry'), Component: Flask },
  { name: 'TestTube', tags: T('test', 'tube', 'lab'), Component: TestTube },
  { name: 'Microscope', tags: T('microscope', 'science', 'lab'), Component: Microscope },
  { name: 'Rocket', tags: T('rocket', 'launch', 'startup'), Component: RocketLaunch },
  { name: 'HardHat', tags: T('hardhat', 'safety', 'worker'), Component: HardHat },
  { name: 'Crane', tags: T('crane', 'construction', 'lift'), Component: Crane },
  { name: 'Screwdriver', tags: T('screwdriver', 'tool'), Component: Screwdriver },
  { name: 'Ruler', tags: T('ruler', 'measure'), Component: Ruler },
  { name: 'Scales', tags: T('scales', 'balance', 'justice'), Component: Scales },
  { name: 'Gavel', tags: T('gavel', 'legal', 'judge'), Component: Gavel },
  { name: 'Bed', tags: T('bed', 'sleep', 'hotel'), Component: Bed },
  { name: 'Couch', tags: T('couch', 'sofa', 'furniture'), Component: Couch },
  { name: 'Armchair', tags: T('armchair', 'chair', 'seat'), Component: Armchair },
  { name: 'ForkKnife', tags: T('food', 'meal', 'restaurant', 'kitchen'), Component: ForkKnife },
  { name: 'Knife', tags: T('knife', 'tool', 'cut'), Component: Knife },
  { name: 'CookingPot', tags: T('pot', 'cook', 'kitchen'), Component: CookingPot },
  { name: 'Broom', tags: T('broom', 'clean'), Component: Broom },
  { name: 'Barbell', tags: T('gym', 'fitness', 'weight'), Component: Barbell },
  { name: 'Football', tags: T('football', 'sport'), Component: Football },
  { name: 'SoccerBall', tags: T('soccer', 'football', 'sport'), Component: SoccerBall },
  { name: 'TennisBall', tags: T('tennis', 'sport'), Component: TennisBall },
  { name: 'Basketball', tags: T('basketball', 'sport'), Component: Basketball },
  { name: 'Volleyball', tags: T('volleyball', 'sport'), Component: Volleyball },
  { name: 'MusicNote', tags: T('music', 'note', 'audio'), Component: MusicNote },
  { name: 'Guitar', tags: T('guitar', 'music', 'instrument'), Component: Guitar },
  { name: 'PaintBrush', tags: T('paint', 'brush', 'art'), Component: PaintBrush },
  { name: 'Palette', tags: T('palette', 'color', 'art'), Component: Palette },
  { name: 'Pencil', tags: T('pencil', 'edit', 'write'), Component: PencilSimple },
  { name: 'Pen', tags: T('pen', 'write'), Component: Pen },
  { name: 'Highlighter', tags: T('highlighter', 'mark'), Component: Highlighter },
  { name: 'Stamp', tags: T('stamp', 'approve', 'seal'), Component: Stamp },
  { name: 'PaperPlaneTilt', tags: T('send', 'plane', 'paper'), Component: PaperPlaneTilt },
];

const ICON_MAP: Map<string, IconEntry> = new Map(ICONS.map((i) => [i.name, i]));

/** Render lookup: returns the React component for a stored icon name, or
 *  `null` if the name isn't a known Phosphor icon (caller falls back to
 *  rendering the raw string — the emoji-backcompat path). */
export function lookupIcon(name: string): IconEntry | null {
  return ICON_MAP.get(name) ?? null;
}
